import { computed, nextTick, ref, watch } from 'vue';
import { i18n } from '@/plugins/i18n';
import { Constants } from '@/cfg/constants';
import alignLeftIcon from '@/assets/icons/sorenson/align-left.svg';
import alignCenterIcon from '@/assets/icons/sorenson/align-center.svg';
import alignRightIcon from '@/assets/icons/sorenson/align-right.svg';
// The file names are swapped relative to the pictures: "width-wider" draws arrows pointing
// in (→|←) and "width-narrow" draws arrows pointing out (|↔|). Picked by picture here.
import widthNarrowIcon from '@/assets/icons/width-wider.svg';
import widthWideIcon from '@/assets/icons/width-narrow.svg';

// Moon / sun icons carried over from the removed Presentation view.
export const MOON_ICON_PATH = 'M8.86288 1.29289C9.15714 1.58715 9.23841 2.03289 9.06693 2.41206C8.53036 3.59854 8.23116 4.91625 8.23116 6.30654C8.23116 11.5324 12.4676 15.7688 17.6935 15.7688C19.0838 15.7688 20.4015 15.4696 21.5879 14.9331C21.9671 14.7616 22.4128 14.8429 22.7071 15.1371C23.0014 15.4314 23.0826 15.8771 22.9112 16.2563C21.1136 20.2311 17.1124 23 12.4623 23C6.13185 23 1 17.8682 1 11.5377C1 6.88761 3.76893 2.88639 7.74371 1.08884C8.12289 0.917367 8.56862 0.998635 8.86288 1.29289ZM6.41336 4.26077C4.32688 5.99712 3 8.61307 3 11.5377C3 16.7636 7.23642 21 12.4623 21C15.3869 21 18.0029 19.6731 19.7392 17.5866C19.0751 17.7064 18.3913 17.7688 17.6935 17.7688C11.363 17.7688 6.23116 12.637 6.23116 6.30654C6.23116 5.60867 6.29363 4.92493 6.41336 4.26077Z';
export const SUN_ICON_PATH = 'M12 1C12.5523 1 13 1.44772 13 2V4C13 4.55228 12.5523 5 12 5C11.4477 5 11 4.55228 11 4V2C11 1.44772 11.4477 1 12 1ZM4.1928 4.1928C4.58332 3.80227 5.21648 3.80227 5.60701 4.1928L7.02122 5.60701C7.41175 5.99753 7.41175 6.6307 7.02122 7.02122C6.6307 7.41175 5.99753 7.41175 5.60701 7.02122L4.1928 5.60701C3.80227 5.21648 3.80227 4.58332 4.1928 4.1928ZM19.8072 4.1928C20.1977 4.58332 20.1977 5.21648 19.8072 5.60701L18.393 7.02122C18.0025 7.41175 17.3693 7.41175 16.9788 7.02122C16.5883 6.6307 16.5883 5.99753 16.9788 5.60701L18.393 4.1928C18.7835 3.80227 19.4167 3.80227 19.8072 4.1928ZM12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8ZM6 12C6 8.68629 8.68629 6 12 6C15.3137 6 18 8.68629 18 12C18 15.3137 15.3137 18 12 18C8.68629 18 6 15.3137 6 12ZM1 12C1 11.4477 1.44772 11 2 11H4C4.55228 11 5 11.4477 5 12C5 12.5523 4.55228 13 4 13H2C1.44772 13 1 12.5523 1 12ZM19 12C19 11.4477 19.4477 11 20 11H22C22.5523 11 23 11.4477 23 12C23 12.5523 22.5523 13 22 13H20C19.4477 13 19 12.5523 19 12ZM7.02122 16.9829C7.41175 17.3734 7.41175 18.0066 7.02122 18.3971L5.60701 19.8113C5.21648 20.2018 4.58332 20.2018 4.1928 19.8113C3.80227 19.4208 3.80227 18.7876 4.1928 18.3971L5.60701 16.9829C5.99753 16.5924 6.6307 16.5924 7.02122 16.9829ZM16.9788 16.9829C17.3693 16.5924 18.0025 16.5924 18.393 16.9829L19.8072 18.3971C20.1977 18.7876 20.1977 19.4208 19.8072 19.8113C19.4167 20.2018 18.7835 20.2018 18.393 19.8113L16.9788 18.3971C16.5883 18.0066 16.5883 17.3734 16.9788 16.9829ZM12 19C12.5523 19 13 19.4477 13 20V22C13 22.5523 12.5523 23 12 23C11.4477 23 11 22.5523 11 22V20C11 19.4477 11.4477 19 12 19Z';

// Translations come from the server, so fall back to English until a key exists there.
export const translate = (key, fallback) => {
  const text = i18n.global.t(key);
  return text !== key ? text : fallback;
};

// Labels and choices shared by the desktop drawer (CaptionSettings) and the mobile bar
// (CaptionSettingsBar).
export function useCaptionSettingsOptions() {
  const captionSettingsTitle = computed(() => translate('caption_settings', 'Caption Settings'));
  // Same wording as the Green Screen toolbar.
  const sizeLabel = 'Font Size';
  const alignmentLabel = computed(() => translate('caption_settings_alignment', 'Alignment'));
  const widthLabel = computed(() => translate('toolbar_width', 'Width'));
  const fontLabel = computed(() => translate('toolbar_font', 'Font'));
  const themeLabel = computed(() => translate('caption_settings_theme', 'Theme'));

  const widthOptions = computed(() => [
    { value: 'narrow', label: translate('caption_settings_width_narrow', 'Narrow'), icon: widthNarrowIcon },
    { value: 'wide', label: translate('caption_settings_width_wide', 'Wide'), icon: widthWideIcon },
  ]);

  const themeOptions = computed(() => [
    { value: 'dark', label: translate('caption_settings_theme_dark', 'Dark'), iconPath: MOON_ICON_PATH },
    { value: 'light', label: translate('caption_settings_theme_light', 'Light'), iconPath: SUN_ICON_PATH },
  ]);

  // Labelled by style rather than font name, and each label is drawn in its own font.
  const fontOptions = computed(() => [
    { value: 'sans', label: translate('caption_settings_font_sans', 'Sans Serif'), css: Constants.CAPTION_FONTS.sans },
    { value: 'serif', label: translate('caption_settings_font_serif', 'Serif'), css: Constants.CAPTION_FONTS.serif },
  ]);

  const alignOptions = computed(() => [
    { value: 'left', label: translate('caption_settings_align_left', 'Left'), icon: alignLeftIcon },
    { value: 'center', label: translate('caption_settings_align_center', 'Center'), icon: alignCenterIcon },
    { value: 'right', label: translate('caption_settings_align_right', 'Right'), icon: alignRightIcon },
  ]);

  return {
    captionSettingsTitle,
    sizeLabel,
    alignmentLabel,
    widthLabel,
    fontLabel,
    themeLabel,
    widthOptions,
    themeOptions,
    fontOptions,
    alignOptions,
  };
}

// Draft value for the typed-in font size. Applied on blur/Enter rather than on every keystroke,
// so typing "1" on the way to "18" doesn't snap to the minimum. The parent clamps the value.
export function useFontSizeDraft(props, emit) {
  const fontSizeDraft = ref(String(Math.round(props.fontSize)));

  watch(() => props.fontSize, (value) => {
    fontSizeDraft.value = String(Math.round(value));
  });

  const commitFontSize = () => {
    const value = Number(fontSizeDraft.value);
    if (fontSizeDraft.value !== '' && Number.isFinite(value)) {
      emit('set-font-size', value);
    }
    // The clamped value may equal the current one, in which case the watcher won't fire.
    nextTick(() => {
      fontSizeDraft.value = String(Math.round(props.fontSize));
    });
  };

  return { fontSizeDraft, commitFontSize };
}
